import { API } from '../net';
import { NET } from '../config';
import type { WorldOp, WelcomeInfo, InvSnapshot, SleeperInfo, StationView } from './protocol';

// The WebSocket transport to a world's Durable Object (spec section 19, Phase M1). It relays the local
// player's pose and reports other players' poses, and (for NetBackend) carries world ops + the shared
// clock. The authority for your own movement stays local (your player never waits on the network); the
// DO relays poses at the client send rate so everyone can see everyone.
//
// It fails soft: if the socket cannot open (offline, or local-only play), the callbacks simply never fire
// and the game runs single-player. Nothing here can break the render loop.

export interface RemotePose { id: string; x: number; y: number; z: number; yaw: number; held: string | null; crouch: boolean; name?: string; down?: boolean; skin?: number; shirt?: number; pants?: number; }
export type RemoteAct = 'swing' | 'fire';
export type PingKind = 'ping' | 'help' | 'follow' | 'base' | 'danger'; // team map/world beacons (owner QoL)

export class WorldConnection {
  private ws: WebSocket | null = null;
  private url: string;
  private myId: string | null = null;
  private lastSent = -Infinity;
  private lx = NaN; private lz = NaN; private lyaw = NaN; private ly = NaN;
  private lheld: string | null = null; private lcrouch = false; private ldown = false;
  private alive = false;
  private wantOpen = false;   // true between connect() and disconnect(); drives auto-reconnect
  private reconnectT = 0;
  // UNACKED OP RING (send-until-acked). Every op gets a client op-id (ck) and sits here until the server's
  // opAck removes it. A (re)connect resends the whole ring in order, so an op created during a blip -- or one
  // swallowed by a zombie socket that looked open -- still reaches the authority exactly once (the server
  // dedupes resends by ck). This replaces the old opQueue, which never filled: every caller checked
  // `connected` first, and ws.send on a dead-but-open socket doesn't throw (audit A2/A3).
  private pendingOps = new Map<string, { op: WorldOp; at: number }>();
  private ckSeq = 0;
  private readonly ckPrefix = Math.random().toString(36).slice(2, 7); // per-session prefix: resends after a reload are new ids
  // Feature-detect the ack protocol off the welcome shape (an old server ships no `drops` array). Against an old
  // server the ring would never drain -- every op resent forever + the 4s unacked check force-reconnecting in a
  // loop -- so acks/resends/zombie-detection switch off and sends degrade to the old fire-and-forget behaviour.
  private serverHasAcks = false;
  // connectAsync waiters: resolved by the next welcome (NetBackend join), rejected on timeout or a kick.
  private welcomeWaiter: { resolve: (w: WelcomeInfo) => void; reject: (e: Error) => void; timer: number } | null = null;
  // --- Liveness (online-only). An auto-response hb ping every hbSec keeps freshness cheap; a silent socket is
  // treated as dead and reconnected; an op unacked past opAckTimeoutMs forces the same path (zombie socket);
  // if reconnects keep failing past the grace window, onFatal fires so the game boots back to the menu. ---
  private lastMsgMs = 0;      // performance.now() of the last message received (heartbeat freshness)
  private clockSentMs = 0;    // performance.now() when we last sent a clock ping (0 = none pending) -> RTT on reply
  private lastPingReqMs = 0;  // performance.now() of the last RTT ping we issued
  private lastHbMs = 0;       // performance.now() of the last auto-response liveness ping
  private downSinceMs = 0;    // performance.now() when the socket first dropped while we still wanted it (0 = up)
  private hbT = 0;            // heartbeat interval id
  private prevHidden = false; // was the tab backgrounded on the previous heartbeat tick (throttled timers -> don't false-kill)
  private pingWaiter: ((rt: number) => void) | null = null; // one-shot resolver for measurePing()
  latencyMs = -1;             // last measured round-trip (ms); -1 = not yet measured. Read by the pause menu.

  onState: (p: RemotePose) => void = () => {};
  onLeave: (id: string) => void = () => {};
  onKick: (reason: string) => void = () => {};
  onWelcome: (id: string) => void = () => {};
  // A RECONNECT welcome arrived (the initial join consumed the first one via connectAsync). Carries the full
  // world payload; NetBackend applies only what this client missed (ops with seq > lastApplied + the removed
  // sets), then the game unfreezes. This is the resync half of the blip story (audit A1).
  onResync: (w: WelcomeInfo) => void = () => {};
  // The server acknowledged one of our ops (ck matched). seq is its position in the persisted log, when persisted --
  // NetBackend tracks it so a resync never re-applies our own ops back onto us.
  onOpAck: (seq: number) => void = () => {};
  onOpNack: (reason: string, op: WorldOp) => void = () => {};
  onAct: (id: string, kind: RemoteAct) => void = () => {};
  onTeamPing: (id: string, kind: PingKind, x: number, z: number) => void = () => {}; // a teammate dropped a beacon
  onShot: (ox: number, oy: number, oz: number, dirs: number[], sp: number) => void = () => {};
  onOp: (actor: string, op: WorldOp, seq?: number) => void = () => {};
  onClock: (timeMs: number, elapsedMs: number, lockedTimeFrac: number | null) => void = () => {};
  onOpen: () => void = () => {};
  onClose: () => void = () => {};
  // FATAL disconnect: reconnects failed for longer than NET.reconnectGraceMs (or the socket went dead and could
  // not be re-established). The game boots back to the menu with this reason -- online-only, no local fallback.
  onFatal: (reason: string) => void = () => {};
  // Live round-trip time (ms) from a ping, for the pause-menu readout. Fires every NET.pingSec.
  onPing: (rttMs: number) => void = () => {};
  // A player disconnected and left a sleeping body (or one already here at welcome). null clears it (woke/removed).
  onSleep: (s: SleeperInfo) => void = () => {};
  onWake: (id: string) => void = () => {};
  // The authority applied damage that dropped this client's OWN hp (server owns sleeper hp, but a LIVE target's
  // hp lives on that client under the M1 trust model, so the server tells the victim to apply it locally).
  onHurt: (dmg: number, sourceX: number, sourceZ: number) => void = () => {};
  // PERSISTENCE: on reconnect the server found our sleeping body and hands it back -- our last position, hp
  // and full inventory. We wake up INTO it (teleport + restore) instead of spawning fresh on the beach.
  onResume: (x: number, y: number, z: number, yaw: number, hp: number, grid: Array<{ id: string; n: number } | null>, bar: Array<{ id: string; n: number } | null>) => void = () => {};
  // The authority sent a sleeper's full snapshot contents so we can render its loot grid (we asked to open it).
  onLootView: (target: string, grid: Array<{ id: string; n: number } | null>, bar: Array<{ id: string; n: number } | null>) => void = () => {};
  // The authority granted a loot take from a body/sleeper: {item,n} to add to our inventory (we requested it).
  onLoot: (item: string, n: number) => void = () => {};
  // Shared placed container (chest/locker/fridge) the server owns: onContainerView carries a full contents
  // snapshot for the grid we have open; onContainerGrant carries a stack we took (add it to our inventory).
  onContainerView: (cid: string, slots: Array<{ id: string; n: number } | null>, st?: StationView) => void = () => {};
  onContainerGrant: (item: string, n: number) => void = () => {};
  // A station's on/lit changed anywhere in the world (someone lit a fire / it burnt out): update its visuals.
  onStationState: (cid: string, on: boolean, lit: boolean) => void = () => {};
  // MODERATION (admin only). The server sends the full player roster in reply to modList; teleport moves OUR
  // player (used by both tpHere = pulled to an admin, and tpTo = the admin jumping to a target).
  onModRoster: (players: Array<Record<string, unknown>>) => void = () => {};
  onTeleport: (x: number, y: number, z: number) => void = () => {};
  // A body (sleeper/corpse) was chopped to nothing: spawn its satchel drop at (x,z) holding the leftovers.
  onBodyChopped: (id: string, x: number, z: number, items: Array<{ id: string; n: number }>) => void = () => {};
  // Animal authority: the server named who owns the herd simulation (the host client). null = nobody yet.
  // We compare it to our own id to decide whether WE run the AI (host) or just apply snapshots (follower).
  onHost: (hostId: string | null) => void = () => {};
  // A host-authored animal snapshot arrived (flat state array) -> followers apply it so herds match everywhere.
  onAnimals: (states: number[]) => void = () => {};
  // A follower asked to damage an animal -> only the HOST receives this and applies it to the real herd.
  onAnimalHit: (index: number, dmg: number) => void = () => {};

  constructor(worldId: string, private readonly playerName = '', private readonly clan: readonly string[] = [], private readonly skin?: { skin: number; shirt: number; pants: number }, private readonly opts?: { probe?: boolean; token?: string }) {
    // https -> wss, http -> ws. The DO route is /api/world/{id}/ws (see server/src/index.ts). The session
    // token rides the URL: the worker verifies it and hands the DO our REAL account identity (anti-spoofing).
    const base = API.replace(/^http/, 'ws');
    const tok = opts?.token ? `?tok=${encodeURIComponent(opts.token)}` : '';
    this.url = `${base}/api/world/${encodeURIComponent(worldId)}/ws${tok}`;
  }

  get connected(): boolean { return this.alive; }
  get id(): string | null { return this.myId; }

  connect(): void { this.wantOpen = true; this.startHeartbeat(); this.open(); }

  // NetBackend join: open the socket and resolve with the full welcome (clock + weather seed + the
  // persisted op log). Rejects after timeoutMs so the caller can fall back to LocalBackend; the socket
  // is left closed on failure (no half-connected presence with an unsynced world).
  connectAsync(timeoutMs: number): Promise<WelcomeInfo> {
    return new Promise<WelcomeInfo>((resolve, reject) => {
      const timer = window.setTimeout(() => {
        this.welcomeWaiter = null;
        this.disconnect();
        reject(new Error('world server welcome timed out'));
      }, timeoutMs);
      this.welcomeWaiter = {
        resolve: (w) => { window.clearTimeout(timer); this.welcomeWaiter = null; resolve(w); },
        // A kick during the join (e.g. "server is full") surfaces its real reason instead of a timeout.
        reject: (e) => { window.clearTimeout(timer); this.welcomeWaiter = null; this.disconnect(); reject(e); },
        timer,
      };
      this.connect();
    });
  }

  private open(): void {
    let ws: WebSocket;
    try {
      ws = new WebSocket(this.url);
    } catch {
      this.scheduleReconnect(); // bad url / transient: keep trying so a blip does not end multiplayer
      return;
    }
    this.ws = ws;
    ws.addEventListener('open', () => {
      this.alive = true;
      this.lastSent = -Infinity; // resend pose after a (re)connect
      this.lastMsgMs = performance.now(); // seed heartbeat freshness
      this.downSinceMs = 0;               // back up: clear the drop timer that feeds the fatal grace window
      this.clockSentMs = 0;               // drop any ping that was pending on the old socket
      // Introduce ourselves so the DO can stamp our display name on relayed poses + ops, and hand it our
      // CLAN set (base-claim mates) so it can gate friendly-fire on sleeper damage. Refreshed via sendClan.
      // A menu preflight sets probe:true -- the server measures/answers but never treats it as the live session.
      try { ws.send(JSON.stringify({ t: 'hello', name: this.playerName, clan: this.clan, skin: this.skin?.skin, shirt: this.skin?.shirt, pants: this.skin?.pants, probe: this.opts?.probe || undefined })); } catch { /* socket gone */ }
      // RESEND every unacked op, in creation order, with its ORIGINAL ck. Ops made during the blip and ops whose
      // ack was lost both land exactly once -- the server dedupes by ck. This closes the "she built it during a
      // hiccup and nobody ever saw it" hole for real (the old opQueue never filled; audit A2).
      // Timestamps refresh to NOW: the zombie check must measure THIS socket's ack latency, not how long the op
      // has existed -- otherwise a heartbeat tick right after reconnect re-kills the fresh, healthy socket.
      for (const [ck, rec] of this.pendingOps) {
        rec.at = performance.now();
        try { ws.send(JSON.stringify({ t: 'op', ck, op: rec.op })); } catch { break; /* socket died mid-flush; next open retries */ }
      }
      this.onOpen();
    });
    ws.addEventListener('message', (ev) => this.onMessage(ev));
    ws.addEventListener('close', () => { this.alive = false; if (this.downSinceMs === 0) this.downSinceMs = performance.now(); this.onClose(); this.scheduleReconnect(); });
    ws.addEventListener('error', () => { /* close will follow; nothing to do */ });
  }

  // Reconnect after a short delay if we still want to be connected (worker redeploy, network blip, DO evict).
  // Online-only: if the socket has been down longer than NET.reconnectGraceMs, stop retrying and fire onFatal so
  // the game boots back to the menu with a reason -- we never silently keep running a stale/disconnected world.
  private scheduleReconnect(): void {
    if (!this.wantOpen || this.reconnectT) return;
    if (this.downSinceMs && performance.now() - this.downSinceMs > NET.reconnectGraceMs) {
      this.wantOpen = false;
      if (this.hbT) { clearInterval(this.hbT); this.hbT = 0; }
      this.onFatal('Lost connection to the server (could not reconnect).');
      return;
    }
    this.reconnectT = (setTimeout(() => { this.reconnectT = 0; if (this.wantOpen) this.open(); }, 1500) as unknown) as number;
  }

  // Heartbeat: ping for RTT every NET.pingSec, and treat a socket that has heard nothing for
  // NET.heartbeatTimeoutMs as dead (close it so the reconnect/fatal path runs). One timer for the whole session.
  private startHeartbeat(): void {
    if (this.hbT) return;
    this.hbT = (setInterval(() => this.heartbeat(), 1000) as unknown) as number;
  }
  private heartbeat(): void {
    if (!this.wantOpen || !this.alive || !this.ws) return;
    const now = performance.now();
    // BACKGROUNDED TAB: the browser throttles this setInterval (to ~1/s then ~1/min), so our ping starves and
    // lastMsgMs goes stale even though the socket is fine. Do NOT declare the socket dead while hidden, and on the
    // FIRST tick after returning, grant a fresh grace window. This kills the false reconnect -> synchronous op-replay
    // that froze the game for a few seconds every time you tabbed back in.
    const hidden = typeof document !== 'undefined' && document.hidden;
    if (hidden) { this.prevHidden = true; return; }
    if (this.prevHidden) { this.prevHidden = false; this.lastMsgMs = now; this.lastPingReqMs = now - NET.pingSec * 1000; this.lastHbMs = now; return; }
    // Liveness ping: answered by the DO runtime's auto-response without waking the DO, so freshness stays cheap
    // at any cadence. The RTT/clock ping below stays on its slower interval for the pause-menu readout.
    if (now - this.lastHbMs > NET.hbSec * 1000) { this.lastHbMs = now; try { this.ws.send('{"t":"hb"}'); } catch { /* socket gone */ } }
    if (now - this.lastPingReqMs > NET.pingSec * 1000) { this.lastPingReqMs = now; this.clockSentMs = 0; this.requestClock(); }
    // ZOMBIE SOCKET: an op unacked past the timeout while the socket still LOOKS open means our sends are going
    // nowhere (dead TCP path). Force the reconnect path -- the open handler resends the whole unacked ring.
    let oldestPending = Infinity;
    if (this.serverHasAcks) for (const rec of this.pendingOps.values()) { oldestPending = rec.at; break; } // insertion order: first = oldest
    const zombie = oldestPending !== Infinity && now - oldestPending > NET.opAckTimeoutMs;
    if (zombie || now - this.lastMsgMs > NET.heartbeatTimeoutMs) { // silent/unacked too long: force close -> reconnect/fatal
      this.alive = false;
      if (this.downSinceMs === 0) this.downSinceMs = now;
      try { this.ws.close(); } catch { /* already closing */ }
      this.onClose();
      this.scheduleReconnect();
    }
  }

  // Measure one round-trip (ms) via a clock ping; resolves with the RTT or `fail` if no reply within timeoutMs.
  // Used by the connect gate to reject an unplayable-ping join before entering the world.
  measurePing(timeoutMs: number, fail = 99999): Promise<number> {
    return new Promise<number>((resolve) => {
      if (!this.alive || !this.ws) { resolve(fail); return; }
      const timer = window.setTimeout(() => { this.pingWaiter = null; resolve(fail); }, timeoutMs);
      this.pingWaiter = (rt) => { window.clearTimeout(timer); this.pingWaiter = null; resolve(rt); };
      this.requestClock();
    });
  }

  private onMessage(ev: MessageEvent): void {
    this.lastMsgMs = performance.now(); // any message keeps the heartbeat fresh (the socket is alive)
    let msg: Record<string, unknown>;
    try { msg = JSON.parse(typeof ev.data === 'string' ? ev.data : '') as Record<string, unknown>; }
    catch { return; }
    switch (msg.t) {
      case 'welcome':
        if (typeof msg.id === 'string') {
          this.myId = msg.id;
          this.onWelcome(msg.id);
          const sleepers = Array.isArray(msg.sleepers) ? (msg.sleepers as SleeperInfo[]) : [];
          const info: WelcomeInfo = {
            id: msg.id,
            timeMs: typeof msg.timeMs === 'number' ? msg.timeMs : Date.now(),
            elapsedMs: typeof msg.elapsedMs === 'number' ? msg.elapsedMs : 0,
            lockedTimeFrac: typeof msg.lockedTimeFrac === 'number' ? msg.lockedTimeFrac : null,
            weatherSeed: typeof msg.weatherSeed === 'number' ? msg.weatherSeed : 0,
            ops: Array.isArray(msg.ops) ? (msg.ops as Array<{ seq?: number; actor: string; op: WorldOp }>) : [],
            sleepers,
            salvaged: Array.isArray(msg.salvaged) ? (msg.salvaged as Array<{ x: number; z: number; rem: number }>) : [],
            foraged: Array.isArray(msg.foraged) ? (msg.foraged as Array<{ x: number; z: number; rem: number }>) : [],
            felled: Array.isArray(msg.felled) ? (msg.felled as Array<{ x: number; z: number; rem: number }>) : [],
            drops: Array.isArray(msg.drops) ? (msg.drops as Array<{ item: string; n: number; x: number; z: number; yaw: number; rem: number }>) : [],
            stations: Array.isArray(msg.stations) ? (msg.stations as Array<{ cid: string; on: boolean; lit: boolean }>) : [],
          };
          this.serverHasAcks = Array.isArray(msg.drops); // new-protocol server: op acks + resend + zombie detection active
          if (!this.serverHasAcks) this.pendingOps.clear();
          if (this.welcomeWaiter) this.welcomeWaiter.resolve(info); // initial join: NetBackend.connect replays everything
          // RECONNECT: the server always ships the full welcome; the old code threw it away here, which is why a
          // blip meant "her ceilings never appear until I relog" (audit A1). Hand it to NetBackend, which applies
          // exactly the ops this client missed, BEFORE re-adding sleepers below (main clears + rebuilds rosters).
          else this.onResync(info);
          // Render every sleeper already in the world at join (the roster rides the welcome) -- EXCEPT our own
          // body: we resume into it (server sends 'resume' + 'wake'), so drawing it here would show a corpse of
          // ourselves lying next to us (the "duplicate person on the ground" bug).
          for (const s of sleepers) if (s && typeof s.id === 'string' && s.id !== this.playerName) this.onSleep(s);
        }
        break;
      case 'opAck': // the authority applied (or deduped) one of our ops: retire it from the resend ring
        if (typeof msg.ck === 'string') {
          this.pendingOps.delete(msg.ck);
          if (typeof msg.seq === 'number') this.onOpAck(msg.seq);
        }
        break;
      case 'opNack':
        if (typeof msg.ck === 'string') {
          const rec = this.pendingOps.get(msg.ck);
          if (rec && msg.reason === 'rate') {
            // TRANSIENT: the token bucket emptied (a reconnect flush can burst past RATE_BURST). The op is still
            // valid -- keep it pending and retry shortly; the bucket refills at 20/s so a queued burst drains.
            rec.at = performance.now();
            const ck = msg.ck;
            window.setTimeout(() => {
              const r = this.pendingOps.get(ck);
              if (r && this.alive && this.ws && this.ws.readyState === WebSocket.OPEN) {
                r.at = performance.now();
                try { this.ws.send(JSON.stringify({ t: 'op', ck, op: r.op })); } catch { /* resent on next open */ }
              }
            }, 1200);
            break;
          }
          // PERMANENT refusal (invalid shape/bounds): stop resending; surface for divergence repair.
          this.pendingOps.delete(msg.ck);
          if (rec) this.onOpNack(typeof msg.reason === 'string' ? msg.reason : 'refused', rec.op);
        }
        break;
      case 'state':
        if (typeof msg.id === 'string' && msg.id !== this.myId && typeof msg.x === 'number') {
          this.onState({ id: msg.id, x: msg.x as number, y: msg.y as number, z: msg.z as number, yaw: (msg.yaw as number) ?? 0, held: (msg.held as string) ?? null, crouch: !!msg.crouch, name: typeof msg.name === 'string' ? msg.name : undefined, down: !!msg.down, skin: typeof msg.skin === 'number' ? msg.skin : undefined, shirt: typeof msg.shirt === 'number' ? msg.shirt : undefined, pants: typeof msg.pants === 'number' ? msg.pants : undefined });
        }
        break;
      case 'act':
        if (typeof msg.id === 'string' && msg.id !== this.myId && (msg.kind === 'swing' || msg.kind === 'fire')) this.onAct(msg.id, msg.kind);
        break;
      case 'ping':
        if (typeof msg.id === 'string' && msg.id !== this.myId && typeof msg.x === 'number' && typeof msg.z === 'number' && typeof msg.kind === 'string') {
          this.onTeamPing(msg.id, msg.kind as PingKind, msg.x as number, msg.z as number);
        }
        break;
      case 'shot':
        // Another player fired: the muzzle + every pellet dir so we draw ALL the tracer streaks on our
        // screen (a shotgun must show all its pellets, not one). Damage is never applied here -- the
        // shooter's client owns the hit (pvp op / server hurt); this is a pure visual across the wire.
        if (typeof msg.id === 'string' && msg.id !== this.myId && typeof msg.ox === 'number' && Array.isArray(msg.dirs)) {
          this.onShot(msg.ox as number, msg.oy as number, msg.oz as number, msg.dirs as number[], (msg.sp as number) ?? 90);
        }
        break;
      case 'op':
        if (typeof msg.id === 'string' && msg.id !== this.myId && msg.op && typeof msg.op === 'object') {
          this.onOp(typeof msg.actor === 'string' ? msg.actor : (msg.id as string), msg.op as WorldOp, typeof msg.seq === 'number' ? msg.seq : undefined);
        }
        break;
      case 'clock':
        if (typeof msg.timeMs === 'number' && typeof msg.elapsedMs === 'number') {
          if (this.clockSentMs > 0) { // this clock reply answers a ping -> record the round-trip time
            this.latencyMs = Math.round(performance.now() - this.clockSentMs);
            this.clockSentMs = 0;
            this.onPing(this.latencyMs);
            if (this.pingWaiter) this.pingWaiter(this.latencyMs);
          }
          this.onClock(msg.timeMs, msg.elapsedMs, typeof msg.lockedTimeFrac === 'number' ? msg.lockedTimeFrac : null);
        }
        break;
      case 'leave':
        if (typeof msg.id === 'string') this.onLeave(msg.id);
        break;
      case 'kick': {
        // The server refused/replaced this session (newer window, or the world is full). Stop reconnecting.
        // Mid-join the reason surfaces through the connect promise; in-game it goes to the kick screen.
        this.wantOpen = false;
        const reason = typeof msg.reason === 'string' ? msg.reason : 'You joined from another window.';
        if (this.welcomeWaiter) this.welcomeWaiter.reject(new Error(reason));
        else this.onKick(reason);
        break;
      }
      case 'sleep':
        // Skip our OWN account's body (same reason as the welcome roster): we never render ourselves as a corpse.
        if (typeof msg.id === 'string' && msg.id !== this.playerName && typeof msg.x === 'number') {
          this.onSleep({
            id: msg.id, name: typeof msg.name === 'string' ? msg.name : '', x: msg.x as number, y: msg.y as number, z: msg.z as number, yaw: (msg.yaw as number) ?? 0, hp: typeof msg.hp === 'number' ? (msg.hp as number) : 100,
            skin: typeof msg.skin === 'number' ? (msg.skin as number) : undefined, shirt: typeof msg.shirt === 'number' ? (msg.shirt as number) : undefined, pants: typeof msg.pants === 'number' ? (msg.pants as number) : undefined,
          });
        }
        break;
      case 'wake':
        if (typeof msg.id === 'string') this.onWake(msg.id);
        break;
      case 'hurt': // the authority applied damage to OUR live player (we own our hp under the M1 trust model)
        if (typeof msg.dmg === 'number') this.onHurt(msg.dmg, (msg.x as number) ?? NaN, (msg.z as number) ?? NaN);
        break;
      case 'resume': // reconnect persistence: wake into our sleeping body (position + hp + inventory)
        if (typeof msg.x === 'number') {
          this.onResume(msg.x as number, msg.y as number, msg.z as number, (msg.yaw as number) ?? 0, typeof msg.hp === 'number' ? (msg.hp as number) : 100,
            Array.isArray(msg.grid) ? (msg.grid as Array<{ id: string; n: number } | null>) : [], Array.isArray(msg.bar) ? (msg.bar as Array<{ id: string; n: number } | null>) : []);
        }
        break;
      case 'lootView': // the authority sent a sleeper's contents so we can show its loot grid
        if (typeof msg.target === 'string') {
          this.onLootView(msg.target, Array.isArray(msg.grid) ? (msg.grid as Array<{ id: string; n: number } | null>) : [], Array.isArray(msg.bar) ? (msg.bar as Array<{ id: string; n: number } | null>) : []);
        }
        break;
      case 'loot': // the authority granted a stack we asked to take off a body/sleeper
        if (typeof msg.item === 'string' && typeof msg.n === 'number') this.onLoot(msg.item, msg.n as number);
        break;
      case 'cView': // full snapshot of a shared container we have open (cOpen reply, our own take/put echo, or a live push)
        if (typeof msg.cid === 'string' && Array.isArray(msg.slots)) this.onContainerView(msg.cid as string, msg.slots as Array<{ id: string; n: number } | null>, msg.st ? (msg.st as StationView) : undefined);
        break;
      case 'stationState': // a fire lit / went dark somewhere: sync its flame + switch for everyone
        if (typeof msg.cid === 'string') this.onStationState(msg.cid as string, !!msg.on, !!msg.lit);
        break;
      case 'modRoster':
        if (Array.isArray(msg.players)) this.onModRoster(msg.players as Array<Record<string, unknown>>);
        break;
      case 'teleport':
        if (typeof msg.x === 'number') this.onTeleport(msg.x as number, (msg.y as number) ?? 0, (msg.z as number) ?? 0);
        break;
      case 'cGrant': // the server granted a stack we took from a shared container: add to our inventory
        if (typeof msg.item === 'string' && typeof msg.n === 'number') this.onContainerGrant(msg.item as string, msg.n as number);
        break;
      case 'chopped': // a body was chopped to nothing -> spawn its satchel with the leftovers
        if (typeof msg.id === 'string' && typeof msg.x === 'number') {
          this.onBodyChopped(msg.id, msg.x as number, (msg.z as number) ?? 0, Array.isArray(msg.items) ? (msg.items as Array<{ id: string; n: number }>) : []);
        }
        break;
      case 'host': // the server named the animal-simulation host (may be us, may be null pre-roster)
        this.onHost(typeof msg.id === 'string' ? msg.id : null);
        break;
      case 'animals': // a host snapshot of the herd (followers apply; the host ignores its own echo -- it never gets one)
        if (Array.isArray(msg.s)) this.onAnimals(msg.s as number[]);
        break;
      case 'animalHit': // a follower's hit routed to us because we are the host: apply it to the real herd
        if (typeof msg.i === 'number' && typeof msg.d === 'number') this.onAnimalHit(msg.i as number, msg.d as number);
        break;
    }
  }

  // Send the local pose, throttled to NET.sendHz and only when it actually changed (a still player sends
  // nothing, so idle presence costs ~0). Call it every frame; it decides whether to actually send.
  sendPose(nowMs: number, x: number, y: number, z: number, yaw: number, held: string | null, crouch: boolean, down: boolean): void {
    if (!this.alive || !this.ws) return;
    if (nowMs - this.lastSent < 1000 / NET.sendHz) return;
    const moved = Math.abs(x - this.lx) + Math.abs(z - this.lz) + Math.abs(y - this.ly) > NET.moveEpsilon;
    const turned = Math.abs(yaw - this.lyaw) > NET.yawEpsilon;
    const changed = held !== this.lheld || crouch !== this.lcrouch || down !== this.ldown; // wielding/posture/downed changes send even while still
    if (!moved && !turned && !changed && this.lastSent > -Infinity) return;
    this.lastSent = nowMs; this.lx = x; this.ly = y; this.lz = z; this.lyaw = yaw; this.lheld = held; this.lcrouch = crouch; this.ldown = down;
    try { this.ws.send(JSON.stringify({ t: 'move', x, y, z, yaw, held, crouch, down })); } catch { /* socket gone */ }
  }

  // One-shot action (swing / fire) -> broadcast so others see the animation. Not throttled (rare, discrete).
  // Broadcast a fired shot: muzzle + every pellet dir (flat x,y,z triples) so all tracers draw remotely.
  sendShot(ox: number, oy: number, oz: number, dirs: number[], sp: number): void {
    this.send({ t: 'shot', ox, oy, oz, dirs, sp });
  }

  sendAct(kind: RemoteAct): void {
    if (!this.alive || !this.ws) return;
    try { this.ws.send(JSON.stringify({ t: 'act', kind })); } catch { /* socket gone */ }
  }

  // Ephemeral team beacon (map ping / quick-comms). Fire-and-forget like sendAct: no ack, no persistence.
  sendPing(kind: PingKind, x: number, z: number): void {
    if (!this.alive || !this.ws) return;
    try { this.ws.send(JSON.stringify({ t: 'ping', kind, x, z })); } catch { /* socket gone */ }
  }

  // A world op (the shared-world side effect of an intent) -> validated + relayed by the DO. SEND-UNTIL-ACKED:
  // the op enters the pending ring first, is sent if the socket looks open, and stays in the ring until the
  // server's opAck retires it -- the open handler resends the ring after any reconnect, and the server dedupes
  // resends by ck. Bounded: past the cap the oldest unacked op is dropped (the grace window will fatal-disconnect
  // to the menu long before a healthy session accumulates 300 unacked ops).
  sendOp(op: WorldOp): void {
    const ck = `${this.ckPrefix}${++this.ckSeq}`;
    if (this.serverHasAcks || !this.alive) { // old server never acks: only park ops while actually down
      this.pendingOps.set(ck, { op, at: performance.now() });
      if (this.pendingOps.size > 300) { const oldest = this.pendingOps.keys().next().value; if (oldest !== undefined) this.pendingOps.delete(oldest); }
    }
    if (this.alive && this.ws && this.ws.readyState === WebSocket.OPEN) {
      try { this.ws.send(JSON.stringify({ t: 'op', ck, op })); } catch { /* stays pending; resent on reconnect */ }
    }
  }

  // Ask the DO for the shared world clock (reply arrives via onClock). Cheap; used for drift checks + RTT ping.
  requestClock(): void {
    if (!this.alive || !this.ws) return;
    if (this.clockSentMs === 0) this.clockSentMs = performance.now(); // stamp the send so the reply yields an RTT
    try { this.ws.send(JSON.stringify({ t: 'clock' })); } catch { /* socket gone */ }
  }

  private send(obj: unknown): void {
    if (!this.alive || !this.ws) return;
    try { this.ws.send(JSON.stringify(obj)); } catch { /* socket gone */ }
  }

  // Ship our compact inventory snapshot to the authority (join + periodically + on death) so our body is
  // lootable by others while we sleep, and so a killed carry becomes corpse loot. hp rides along so a relog
  // resumes at the health we logged off with instead of a free full heal (audit F4). Server-stored; clamped there.
  sendSnapshot(snap: InvSnapshot, hp?: number): void { this.send({ t: 'snapshot', snap, hp, lat: this.latencyMs >= 0 ? this.latencyMs : undefined }); }

  // Damage a remote player OR a sleeper (server owns sleeper hp; a live target's own client owns its hp, M1 trust).
  sendPvp(target: string, dmg: number, part?: 'body' | 'head'): void { this.send({ t: 'pvp', target, dmg, part }); }

  // Ask the authority for a sleeper's contents (reply arrives via onLootView) to open its loot grid.
  sendLootOpen(target: string): void { this.send({ t: 'lootOpen', target }); }

  // Take one stack (slot-exact, idempotent) off a body/sleeper snapshot. Server removes + grants via onLoot.
  sendLoot(target: string, slot: number, bar: boolean): void { this.send({ t: 'lootTake', target, slot, bar }); }

  // Shared containers (chest/locker/fridge): open (fetch contents via onContainerView), take one slot (server
  // grants via onContainerGrant), put n of an item (slot -1 = server picks the slot). Server owns the contents.
  sendContainerOpen(cid: string): void { this.send({ t: 'cOpen', cid }); }
  sendContainerTake(cid: string, slot: number): void { this.send({ t: 'cTake', cid, slot }); }
  sendContainerPut(cid: string, slot: number, item: string, n: number): void { this.send({ t: 'cPut', cid, slot, item, n }); }
  sendContainerSet(cid: string, on: boolean): void { this.send({ t: 'cSet', cid, on }); }   // station on/off toggle
  sendContainerClose(cid: string): void { this.send({ t: 'cClose', cid }); }               // stop live view pushes
  sendContainerSort(cid: string): void { this.send({ t: 'cSort', cid }); }                 // merge+alphabetize server-side
  // MODERATION (admin only; the server re-checks the token→owner, so a non-admin sending these does nothing).
  requestModRoster(): void { this.send({ t: 'modList' }); }
  sendMod(action: string, target: string, durMs?: number): void { this.send({ t: 'mod', action, target, durMs }); }

  // The 3rd melee hit destroyed a body -> ask the authority to spawn its satchel (server owns sleeper contents).
  sendChopBody(target: string): void { this.send({ t: 'chopBody', target }); }

  // Refresh our clan set on the server after a claim membership change (add/remove/claim/clear).
  sendClan(clan: readonly string[]): void { this.send({ t: 'clan', clan }); }

  // We DIED (still connected): ask the authority to leave a lootable body (a sleeper with hp 0) at the death
  // spot, seeded from our last shipped snapshot, and to drop our live avatar for everyone else. Others then
  // see + loot + chop the body through the existing body path; we run back to loot our own corpse too.
  sendDied(x: number, y: number, z: number, yaw: number): void { this.send({ t: 'died', x, y, z, yaw }); }

  // HOST ONLY: broadcast the authoritative herd snapshot (flat state array) so every follower matches us.
  sendAnimals(states: number[]): void { this.send({ t: 'animals', s: states }); }

  // FOLLOWER ONLY: ask the host to damage animal #index by dmg (the host owns kills + the meat drop).
  sendAnimalHit(index: number, dmg: number): void { this.send({ t: 'animalHit', i: index, d: dmg }); }

  disconnect(): void {
    this.wantOpen = false;
    this.pendingOps.clear(); // a deliberate disconnect abandons any unacked ops (the next connect is a fresh session)
    if (this.reconnectT) { clearTimeout(this.reconnectT); this.reconnectT = 0; }
    if (this.hbT) { clearInterval(this.hbT); this.hbT = 0; }
    try { this.ws?.close(); } catch { /* ignore */ }
    this.ws = null; this.alive = false;
  }
}
